import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { StateService } from '@uirouter/core';
import { Company, IdentityService, LicenseType, Permissions, Property, SessionService } from 'account-hybrid/core/authentication';
import { LicenseService } from 'account-hybrid/core/license.service';
import { DataGranularity } from 'account-hybrid/features/account-settings/enums/data-granularity.enum';
import { ExperienceLicensePermission } from 'account-hybrid/features/experiences/enums/experience-license-permission.enum';
import * as fromRoot from 'account-hybrid/store/reducers/index';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { getSpaceAttributesFromSpace, SpacesService, SpaceType, UiRouterStateSpaceAttributes } from '../../spaces';
import { SetIsVisible } from '../../spaces/store/actions/spaces.actions';
import * as fromSpaces from '../../spaces/store/reducers/spaces.reducer';


@Component({
    selector: 'ft-main-nav',
    templateUrl: './main-nav.component.html',
    styleUrls: ['./main-nav.component.scss'],
    standalone: false
})
export class MainNavComponent implements OnInit {
    @Input() spacing = false;
    @Input() sticky = false;
    @Input() logoVisible = true;
    @Input() menuVisible = true;
    @Input() sideMenuVisible = true;
    @Input() modifierClass: string | string[];
    spaces$: Observable<fromSpaces.State>;
    spacesNumber: number;
    isFliptoStaff: boolean;
    isCompanyContextAvailable: boolean;
    isCollaborativeSpace: boolean;
    isPropertySpace = this.spacesService.isPropertySpace();
    spaceAttributes: UiRouterStateSpaceAttributes;
    company: Company;
    property: Property;
    DataGranularity = DataGranularity;
    isSpacetimeFliptoOnly = false;
    isSpacetimeDisabled = false;

    constructor(public spacesService: SpacesService,
                private sessionService: SessionService,
                public identityService: IdentityService,
                private licenseService: LicenseService,
                public store: Store<fromRoot.State>,
                private cdr: ChangeDetectorRef,
                public stateService: StateService) {
        this.isFliptoStaff = this.identityService.isFliptoStaff();
        this.isCompanyContextAvailable = !!this.spacesService.getCompanyUuid() && this.spacesService.current.type !== SpaceType.Collaborative;
        this.isCollaborativeSpace = !!this.spacesService.getCompanyUuid() && this.spacesService.current.type === SpaceType.Collaborative;
        this.company = this.sessionService.getCompany(this.spacesService.getCompanyUuid());
        this.property = this.sessionService.getProperty(this.spacesService.getPropertyUuid());
        this.updateSpacetimeLicenseState();
        this.sessionService.licenseChanged$
            .subscribe(() => {
                this.property = this.sessionService.getProperty(this.spacesService.getPropertyUuid());
                this.updateSpacetimeLicenseState();
                this.cdr.markForCheck();
            });
    }

    ngOnInit(): void {
        this.spaces$ = this.store.pipe(select(fromRoot.selectSpaces));
        this.spaces$.pipe(tap(state => this.spacesNumber = (state.allSpaces.length + state.collaborativeSpaces.length))).subscribe();
        if (this.spacesService.current) {
            this.spaceAttributes = getSpaceAttributesFromSpace(this.spacesService.current);
        }
    }

    spaceClicked() {
        if (this.spacesNumber > 1) {
            this.store.dispatch(new SetIsVisible(true));
        } else {
            this.stateService.go('hub', getSpaceAttributesFromSpace(this.spacesService.current));
        }
    }

    isLocalLicenseActive() {
        return Boolean(this.property?.LicensesExtended?.find(license => license.LicenseId === LicenseType.Experiences)?.IsActive || false);
    }

    isOGLicenseActive() {
        return ((this.property?.LicensesExtended?.find(license => license.LicenseId === LicenseType.Experiences)?.Permissions & ExperienceLicensePermission.AllOG) != 0);
    }

    private updateSpacetimeLicenseState() {
        this.isSpacetimeFliptoOnly = this.licenseService.isCurrentSpaceLicenseFliptoOnly(LicenseType.Spacetime);
        this.isSpacetimeDisabled = this.licenseService.isCurrentSpaceLicenseDisabled(LicenseType.Spacetime);
    }

    get allPrimaryLicensesHidden(): boolean {
        // Advocacy is property-scoped, so it never shows on a company space
        return (this.spacesService.isCompanySpace() || this.licenseService.isCurrentSpaceLicenseHidden(LicenseType.Advocacy))
            && this.licenseService.isCurrentSpaceLicenseHidden(LicenseType.Discovery)
            && this.licenseService.isCurrentSpaceLicenseHidden(LicenseType.Spacetime);
    }

    get canAccessPrivacyRequests(): boolean {
        return this.isFliptoStaff || this.identityService.hasPermission(Permissions.SuperUser);
    }
}
